import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, ReplaySubject, Subject, tap, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AvailableTypesTrackColors } from '@common/components/create-object/tabs/object-sensors-tab/create-sensors.service';
import { PortalTabsE } from '@common/utils/consts/monitoring-tabs-config';
import { setParamsCreateTrackForMessages } from '@store/actions/messages.actions';
import { setParamsCreateTrack } from '@store/actions/tracks.actions';
import { AvailableMessagesParam } from '@store/reducers/messages.reducer';
import { setParamsCreateTrackForReports } from '@store/reports/reports.actions';

export enum RoadEventTypes {
  overSpeed = 'overSpeed',
  parking = 'parking',
  plum = 'plum',
  refuel = 'refuel',
  stop = 'stop',
}

export type MessagesTrackTuple =
  [number, number, number, string] | [number, number, number, string, number];

export interface ResponseCreateTrack {
  track: any;
  trips: any;
  parkings: any;
  refuels: any;
  plums: any;
  stops: any;
  overSpeeds: any;
  sensors: any;
  additional: any;
}

export interface RequestCreateTrack {
  object_id: any;
  start_date: any;
  end_date: any;
  track_type: any;
  detect_trips: any;
  sensor_id?: any;
  withStops: any;
  withParkings: any;
  withRefuels: any;
  withPlums: any;
  withOverSpeed: any;
  cacheKey?: string;
}

export type CreateTrackParams = {
  cacheKey?: string;
  cacheExpired?: string;
  objectId: number;
  startDate: string;
  endDate: string;
  trackType: AvailableTypesTrackColors;
  sensorId?: number;
  detectorTrips: boolean;
  isRequestMessagesParam?: boolean;
  messagesParamsType?: AvailableMessagesParam;
  trackLineWeight?: number;
  withStartEnds?: boolean;
  colorSingleTrack?: string;
} & Partial<Record<RoadEventTypes, boolean>>;

export interface RoadEventRequest {
  reportId: string; // идентификатор отчета
  startDate: string; // начальные дата-время периода в запросе
  endDate: string; // конечные дата-время периода в запросе
  objectId: number; // идентификатор объекта (проверяются права)
  withConnectionLosses: boolean; // потери связи
  withEngineHours: boolean; // моточасы (зажигание)
  withOverSpeed: boolean; // превышения скорости
  withParkings: boolean; // стоянки
  withPlums: boolean; // сливы
  withRefuels: boolean; // заправки
  withStops: boolean; // остановки
  withTrips: boolean; // поездки
}

@Injectable({
  providedIn: 'root',
})
export class TracksService {
  private readonly httpClient = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  public objectIdForFastTrack$ = new BehaviorSubject<any>(null);
  public selectedTrack = new ReplaySubject(1);
  public deleteTrack = new Subject();
  public closeTrack = new Subject();
  public addTrack = new Subject();
  public zoomOnTrack = new Subject();

  public addresses: any = {};

  textErrOnNotCoordinates = 'There is no data for the selected period';

  createTrack(params: CreateTrackParams): Observable<ResponseCreateTrack> {
    const body: RequestCreateTrack = {
      object_id: params.objectId,
      start_date: params.startDate,
      end_date: params.endDate,
      track_type: params.trackType,
      detect_trips: params.detectorTrips,
      sensor_id: params.sensorId,
      withStops: params.stop,
      withParkings: params.parking,
      withRefuels: params.refuel,
      withPlums: params.plum,
      withOverSpeed: params.overSpeed,
    };

    if (params.cacheKey) {
      body['cacheKey'] = params.cacheKey;
    }

    return this.httpClient.post('tracks/create/', body).pipe(
      tap((track: any) => {
        const currentPage = this.router.url.split('/portal/')[1];

        if (
          track.track.messagesCoordinates.length === 0 &&
          !currentPage.startsWith(PortalTabsE.Reports)
        ) {
          throw new Error(this.textErrOnNotCoordinates);
        } else {
          if (currentPage.startsWith(PortalTabsE.Tracks)) {
            this.store.dispatch(
              setParamsCreateTrack({
                params: {
                  ...params,
                  cacheExpired: track.track.cacheExpired,
                },
                cacheKey: track.track.cacheKey,
              }),
            );
          } else if (currentPage.startsWith(PortalTabsE.Messages)) {
            this.store.dispatch(
              setParamsCreateTrackForMessages({
                params: {
                  ...params,
                  cacheExpired: track.track.cacheExpired,
                },
                cacheKey: track.track.cacheKey,
              }),
            );
          } else if (currentPage.startsWith(PortalTabsE.Reports)) {
            this.store.dispatch(
              setParamsCreateTrackForReports({
                params: {
                  ...params,
                  cacheExpired: track.track.cacheExpired,
                },
                cacheKey: track.track.cacheKey,
              }),
            );
          }
        }
      }),
      map((track: any) => {
        return {
          ...track,
          additional: {
            isRequestMessagesParam: params?.isRequestMessagesParam,
            messagesParamsType: params?.messagesParamsType,
            detectorTrips: params.detectorTrips,
            trackLineWeight: params?.trackLineWeight,
            trackType: params.trackType,
            colorSingleTrack: params?.colorSingleTrack,
            withStartEnds: params?.withStartEnds,
            sensorId: params?.sensorId,
          },
        };
      }),

      catchError((error: any) => {
        return throwError(error);
      }),
    );
  }

  deleteTracksFromCache(cacheKeys: string[]) {
    return this.httpClient.post(`tracks/delete/`, { uuidKeys: cacheKeys });
  }

  getRoadEvents(body: RoadEventRequest) {
    return this.httpClient.post('road_events/get/', body);
  }
}
